import type React from "react";
import { Avatar, Box, Stack, Typography } from "@mui/material";
import type { DeceasedPerson } from "../../../../../../../../bookings/types/edit-booking";
import type { IStreamDetails } from "../../../../types/memorial-types";
import muzukuruLogo from "../../../../../../../../../assets/muzukurublack.png";
import CountdownTimer from "../../../../shared-ui/CountdownTimer";

function formatYear(dateStr?: string | null): string {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  return Number.isNaN(d.getTime()) ? "—" : String(d.getFullYear());
}

interface BrandedHeaderProps {
  stream: IStreamDetails;
  deceasedData: DeceasedPerson | null;
  funeralHomeName: string;
  funeralHomeLogo?: string | null;
  funeralHomeColor: string;
}

const BrandedHeader: React.FC<BrandedHeaderProps> = ({
  stream,
  deceasedData,
  funeralHomeName,
  funeralHomeLogo,
  funeralHomeColor,
}) => {
  const deceased = stream.booking.deceased[0];
  const fullName = [
    deceasedData?.first_name || deceased?.first_name,
    deceasedData?.second_name,
    deceasedData?.surname || deceased?.surname,
  ]
    .filter(Boolean)
    .join(" ");

  const photoSrc =
    deceasedData?.person_image || deceased?.person_image || undefined;
  const dob = deceasedData?.date_of_birth || deceased?.date_of_birth;
  const dod = deceasedData?.date_of_death || deceased?.date_of_death;

  const getEventDate = () => {
    if (stream?.date && stream?.start) {
      const startTime = stream.start.length === 5 ? `${stream.start}:00` : stream.start;
      return `${stream.date}T${startTime}`;
    }
    if (stream?.event_details?.start_date_of_event && stream?.event_details?.start_time) {
      const startTime = stream.event_details.start_time.length === 5 ? `${stream.event_details.start_time}:00` : stream.event_details.start_time;
      return `${stream.event_details.start_date_of_event}T${startTime}`;
    }
    return (
      stream?.service?.date_of_service ||
      stream?.event_details?.start_date_of_event ||
      stream?.booking?.events?.[0]?.start_date_of_event
    );
  };
  const eventDate = getEventDate();

  return (
    <Box
      sx={{
        width: "100%",
        bgcolor: "#fff",
        py: { xs: 2, md: 3 },
      }}
    >
      {/* Brand bar */}
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ height: { xs: 26, md: 60 } }}>
          {funeralHomeLogo ? (
            <Box
              component="img"
              src={funeralHomeLogo}
              alt={funeralHomeName}
              sx={{ height: { xs: 26, md: 60 }, objectFit: "contain" }}
            />
          ) : (
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: { xs: "16px", md: "20px" },
                textTransform: "uppercase",
                color: "#1b1b1b",
              }}
            >
              {funeralHomeName}
            </Typography>
          )}
        </Stack>

        <Box sx={{ position: "relative", height: { xs: 26, md: 60 } }}>
          <Box
            component="img"
            src={muzukuruLogo}
            alt="Muzukuru"
            sx={{ height: { xs: 26, md: 60 } }}
          />
          {eventDate && (
            <Box
              sx={{
                display: { xs: "none", sm: "block" },
                position: "absolute",
                top: "100%",
                right: 0,
                pt: 0.5,
                whiteSpace: "nowrap",
              }}
            >
              <Typography
                sx={{
                  fontSize: "9px",
                  fontWeight: 600,
                  letterSpacing: 1,
                  textTransform: "uppercase",
                  color: "text.secondary",
                  textAlign: "center",
                  mb: 0.25,
                }}
              >
                Starts in
              </Typography>
              <CountdownTimer eventDate={eventDate} color="#614338" labelColor="text.secondary" size="sm" />
            </Box>
          )}
        </Box>
      </Stack>

      {/* Deceased header */}
      <Stack alignItems="center" spacing={0.5} sx={{ mt: { xs: 3, md: 4 } }}>
        <Avatar
          src={photoSrc}
          sx={{
            width: { xs: 88, md: 120 },
            height: { xs: 88, md: 120 },
            border: `3px solid ${funeralHomeColor}`,
          }}
        />
        <Typography
          sx={{
            fontStyle: "italic",
            fontFamily: '"Dancing Script", "Freehand", cursive',
            fontSize: { xs: "1.1rem", md: "1.3rem" },
            color: "text.secondary",
            mt: 1,
          }}
        >
          In Loving Memory Of
        </Typography>
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: { xs: "1.4rem", md: "1.8rem" },
            color: "#1b1b1b",
          }}
        >
          {fullName || "N/A"}
        </Typography>
        <Typography sx={{ fontSize: "14px", color: "text.secondary" }}>
          {formatYear(dob)} — {formatYear(dod)}
        </Typography>
      </Stack>
    </Box>
  );
};

export default BrandedHeader;
